import Flickity from "flickity";
import "flickity/css/flickity.css";

import "flickity-fade";
import "flickity-fade/flickity-fade.css";

function parseCarouselSettings(carouselEl) {
  try {
    const attr = carouselEl.getAttribute("data-carousel-settings");
    return attr ? JSON.parse(attr) : null;
  } catch (error) {
    console.warn("Invalid data-carousel-settings JSON.", error);
    return null;
  }
}

function normalizeItemHeights(carouselItemEls) {
  if (!carouselItemEls.length) return;

  let tallestHeight = 0;

  carouselItemEls.forEach((carouselItemEl) => {
    tallestHeight = Math.max(tallestHeight, carouselItemEl.offsetHeight);
  });

  carouselItemEls.forEach((carouselItemEl) => {
    carouselItemEl.style.height = `${tallestHeight}px`;
  });
}

function buildFlickityOptions(carouselSettings) {
  const isFade = !!carouselSettings.fade;
  const isFreeScroll = !!carouselSettings.freeScroll && !isFade;
  const isAutoScroll =
    !!carouselSettings.autoScroll &&
    !isFade &&
    !isFreeScroll &&
    !carouselSettings.autoPlay;
  const isAutoPlay = !!carouselSettings.autoPlay && !isAutoScroll;

  return {
    isFade,
    isFreeScroll,
    isAutoScroll,
    isAutoPlay,
    flickityOptions: {
      accessibility: true,
      resize: true,
      percentPosition: true,
      setGallerySize: true,
      fade: isFade,
      cellAlign:
        !isFade && !isFreeScroll
          ? carouselSettings.cellAlign || "center"
          : "left",
      contain: !isFade && !isFreeScroll ? !!carouselSettings.contain : false,
      freeScroll: isFreeScroll,
      wrapAround:
        typeof carouselSettings.wrapAround === "boolean"
          ? carouselSettings.wrapAround
          : true,
      draggable:
        typeof carouselSettings.draggable === "boolean"
          ? carouselSettings.draggable
          : true,
      prevNextButtons: false,
      pageDots:
        typeof carouselSettings.pageDots === "boolean"
          ? carouselSettings.pageDots
          : false,
      autoPlay: isAutoPlay ? carouselSettings.autoPlay : false,
      pauseAutoPlayOnHover:
        typeof carouselSettings.pauseAutoPlayOnHover === "boolean"
          ? carouselSettings.pauseAutoPlayOnHover
          : true,
      imagesLoaded:
        typeof carouselSettings.imagesLoaded === "boolean"
          ? carouselSettings.imagesLoaded
          : true,
    },
  };
}

function setupOverflow(carouselEl, carouselListEl, carouselSettings, isFade) {
  if (!carouselSettings.showOverFlow || isFade) return;

  const flickityViewportEl = carouselListEl.querySelector(".flickity-viewport");

  carouselListEl.style.overflow = "visible";

  if (flickityViewportEl) {
    flickityViewportEl.style.overflow = "visible";
  }
}

function setupNavigation(
  carouselEl,
  carouselNavEl,
  carouselPrevBtnEl,
  carouselNextBtnEl,
  flickityInstance,
  carouselSettings,
) {
  if (carouselNavEl) {
    carouselNavEl.style.display = carouselSettings.showNavigation ? "" : "none";
  }

  if (carouselPrevBtnEl) {
    carouselPrevBtnEl.addEventListener("click", () => {
      flickityInstance.previous();
    });
  }

  if (carouselNextBtnEl) {
    carouselNextBtnEl.addEventListener("click", () => {
      flickityInstance.next();
    });
  }

  if (flickityInstance.slides.length <= 1 && carouselNavEl) {
    carouselNavEl.style.display = "none";
  }
}

function setupAutoScroll(carouselEl, flickityInstance, carouselSettings) {
  let requestId = null;
  let isPaused = false;

  const autoScrollSpeed =
    typeof carouselSettings.autoScrollSpeed === "number"
      ? carouselSettings.autoScrollSpeed
      : 0.7;

  flickityInstance.x = 0;
  flickityInstance.positionSlider();

  function play() {
    if (isPaused) return;

    flickityInstance.x -= autoScrollSpeed;
    flickityInstance.positionSlider();

    requestId = window.requestAnimationFrame(play);
  }

  function start() {
    if (requestId) return;
    isPaused = false;
    requestId = window.requestAnimationFrame(play);
  }

  function pause() {
    isPaused = true;

    if (requestId) {
      window.cancelAnimationFrame(requestId);
      requestId = null;
    }
  }

  start();

  if (carouselSettings.pauseAutoPlayOnHover !== false) {
    carouselEl.addEventListener("mouseenter", pause);
    carouselEl.addEventListener("mouseleave", start);
    carouselEl.addEventListener("focusin", pause);
    carouselEl.addEventListener("focusout", start);
    carouselEl.addEventListener("touchstart", pause, { passive: true });
    carouselEl.addEventListener("touchend", start);
  }

  window.addEventListener("blur", pause);
  window.addEventListener("focus", start);

  return {
    start,
    pause,
    destroy() {
      pause();
      window.removeEventListener("blur", pause);
      window.removeEventListener("focus", start);
    },
  };
}

function initCarousel(carouselEl, customSettings = null) {
  let carouselListEl = carouselEl.querySelector("[data-carousel-list]");
  if (!carouselListEl) carouselListEl = carouselEl;

  let carouselItemEls = [
    ...carouselEl.querySelectorAll("[data-carousel-item]"),
  ];
  if (!carouselItemEls.length) carouselItemEls = [...carouselListEl.children];

  const carouselNavEl = carouselEl.querySelector(".carousel__nav");
  const carouselPrevBtnEl = carouselEl.querySelector("[data-carousel-prev]");
  const carouselNextBtnEl = carouselEl.querySelector("[data-carousel-next]");

  const carouselSettings =
    customSettings || parseCarouselSettings(carouselEl) || {};

  if (carouselSettings.disableDesktop && window.innerWidth > 992) {
    carouselEl.classList.remove("carousel");
    carouselListEl.classList.remove("carousel__list");
    carouselItemEls.forEach((el) => el.classList.remove("carousel__item"));
    if (carouselNavEl) carouselNavEl.remove();
    return null;
  }

  if (
    carouselSettings.disableTablet &&
    window.innerWidth > 769 &&
    window.innerWidth < 991
  ) {
    carouselEl.classList.remove("carousel");
    carouselListEl.classList.remove("carousel__list");
    carouselItemEls.forEach((el) => el.classList.remove("carousel__item"));
    if (carouselNavEl) carouselNavEl.remove();
    return null;
  }

  if (carouselSettings.disableMobile && window.innerWidth < 768) {
    carouselEl.classList.remove("carousel");
    carouselListEl.classList.remove("carousel__list");
    carouselItemEls.forEach((el) => el.classList.remove("carousel__item"));
    if (carouselNavEl) carouselNavEl.remove();
    return null;
  }

  carouselListEl.style.display = "block";
  carouselListEl.style.setProperty("display", "block", "important");

  normalizeItemHeights(carouselItemEls);

  const { isFade, isAutoScroll, flickityOptions } =
    buildFlickityOptions(carouselSettings);

  const flickityInstance = new Flickity(carouselListEl, flickityOptions);

  setupOverflow(carouselEl, carouselListEl, carouselSettings, isFade);

  setupNavigation(
    carouselEl,
    carouselNavEl,
    carouselPrevBtnEl,
    carouselNextBtnEl,
    flickityInstance,
    carouselSettings,
  );

  let autoScrollControls = null;

  if (flickityInstance.slides.length > 1 && isAutoScroll) {
    autoScrollControls = setupAutoScroll(
      carouselEl,
      flickityInstance,
      carouselSettings,
    );
  }

  return {
    element: carouselEl,
    list: carouselListEl,
    flickity: flickityInstance,
    autoScroll: autoScrollControls,
    destroy() {
      if (autoScrollControls) {
        autoScrollControls.destroy();
      }
      flickityInstance.destroy();
    },
  };
}

function initCarousels() {
  const carouselEls = document.querySelectorAll("[data-carousel]");
  if (!carouselEls.length) return [];

  if (typeof Flickity === "undefined") {
    console.warn("Flickity is not loaded.");
    return [];
  }

  return [...carouselEls]
    .map((carouselEl) => initCarousel(carouselEl))
    .filter(Boolean);
}

document.addEventListener("DOMContentLoaded", () => {
  initCarousels();
});

window.initCustomFlickity = function (selector, settings = {}) {
  const el = document.querySelector(selector);
  if (!el) {
    console.warn(
      `initCustomFlickity: Element not found for selector "${selector}"`,
    );
    return null;
  }
  return initCarousel(el, settings);
};
document.addEventListener("DOMContentLoaded", () => {
  if (window.innerWidth < 768) {
    const homeExpEl = document.querySelector(".home--experience--container");
    if (homeExpEl) {
      window.initCustomFlickity(".home--experience--container", {
        cellAlign: "left",
        contain: true,
        pageDots: false,
        wrapAround: false,
      });
    }
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const archiveSelector = ".events-archive--container";
  const archiveEl = document.querySelector(archiveSelector);

  if (archiveEl) {
    archiveEl.style.display = "block";
    const flktyArchive = new Flickity(archiveSelector, {
      cellAlign: "left",
      contain: true,
      prevNextButtons: false,
      pageDots: false,
      imagesLoaded: true,
    });

    const prevBtn = document.querySelector(".events-archive--nav.prev");
    const nextBtn = document.querySelector(".events-archive--nav.next");

    if (prevBtn)
      prevBtn.addEventListener("click", () => flktyArchive.previous());
    if (nextBtn) nextBtn.addEventListener("click", () => flktyArchive.next());
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const packagesSelector = ".home--packages--slider";
  const packagesEl = document.querySelector(packagesSelector);

  if (packagesEl) {
    const seeMoreIncludesEls = document.querySelectorAll(
      ".home--packages--see-more",
    );
    const CardPakagesEls = document.querySelectorAll(".home--packages--wrap");

    CardPakagesEls.forEach((el) => {
      const elementHeight = el.offsetHeight;
      el.style.height = `${elementHeight}px`;
      el.setAttribute("actual-heigth", elementHeight);
      el.classList.add("some-hidden");
    });

    function setSliderViewportHeight(active, clickIndex) {
      const viewportEl = document.querySelector(
        ".home--packages--container .flickity-viewport",
      );
      if (!viewportEl) return;

      let biggerHeight = 0;
      CardPakagesEls.forEach((el, index) => {
        let elHeigth;
        if (active) {
          elHeigth =
            clickIndex === index
              ? parseInt(el.getAttribute("actual-heigth"))
              : el.offsetHeight;
        } else {
          elHeigth = clickIndex === index ? 435 : el.offsetHeight;
        }
        if (elHeigth > biggerHeight) biggerHeight = elHeigth;
      });
      viewportEl.style.height = `${biggerHeight}px`;
    }

    seeMoreIncludesEls.forEach((el, index) => {
      let active = false;
      el.addEventListener("click", (e) => {
        active = !active;
        const textSeeMoreEl = e.currentTarget.querySelector(".ct-text-block");
        const iconSeeMoreEl = e.currentTarget.querySelector("svg");

        setSliderViewportHeight(active, index);

        if (active) {
          if (CardPakagesEls[index])
            CardPakagesEls[index].classList.remove("some-hidden");
          if (textSeeMoreEl) textSeeMoreEl.innerHTML = "SEE Less inclusion";
          if (iconSeeMoreEl) iconSeeMoreEl.style.rotate = "180deg";
        } else {
          if (textSeeMoreEl) textSeeMoreEl.innerHTML = "SEE ALL inclusion";
          if (CardPakagesEls[index])
            CardPakagesEls[index].classList.add("some-hidden");
          if (iconSeeMoreEl) iconSeeMoreEl.style.rotate = "";
        }
      });
    });

    packagesEl.style.display = "block";
    new Flickity(packagesSelector, {
      cellAlign: "left",
      contain: true,
      prevNextButtons: false,
      pageDots: false,
    });
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const mainSliderEl = document.querySelector(
    ".home--specifications--container",
  );
  const featuresEls = document.querySelectorAll(
    ".page-home--specification--wrap",
  );
  const prevBtn = document.querySelector(".home--specifications--prev");
  const nextBtn = document.querySelector(".home--specifications--next");

  if (mainSliderEl) {
    mainSliderEl.style.display = "block";

    const mainBoat = new Flickity(mainSliderEl, {
      cellAlign: "left",
      contain: true,
      prevNextButtons: false,
      pageDots: false,
      imagesLoaded: true,
    });

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        mainBoat.next();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        mainBoat.previous();
      });
    }

    const setfeatureActive = (activeIndex) => {
      featuresEls.forEach((el, index) => {
        if (activeIndex === index) {
          el.style.display = "flex";
        } else {
          el.style.display = "none";
        }
      });
    };

    mainBoat.on("change", setfeatureActive);
    setfeatureActive(0);
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const packagesSelector = ".home--packages--container";
  const packagesEl = document.querySelector(packagesSelector);

  if (packagesEl) {
    const seeMoreIncludesEls = document.querySelectorAll(
      ".home--packages--see-more",
    );
    const CardPakagesEls = document.querySelectorAll(".home--packages--wrap");

    CardPakagesEls.forEach((el) => {
      const elementHeight = el.offsetHeight;
      el.style.height = `${elementHeight}px`;
      el.setAttribute("actual-heigth", elementHeight);
      el.classList.add("some-hidden");
    });

    function setSliderViewportHeight(active, clickIndex) {
      const viewportEl = document.querySelector(
        ".home--packages--container .flickity-viewport",
      );
      if (!viewportEl) return;

      let biggerHeight = 0;
      CardPakagesEls.forEach((el, index) => {
        let elHeigth;
        if (active) {
          elHeigth =
            clickIndex === index
              ? parseInt(el.getAttribute("actual-heigth"))
              : el.offsetHeight;
        } else {
          elHeigth = clickIndex === index ? 435 : el.offsetHeight;
        }
        if (elHeigth > biggerHeight) biggerHeight = elHeigth;
      });
      viewportEl.style.height = `${biggerHeight}px`;
    }

    seeMoreIncludesEls.forEach((el, index) => {
      let active = false;
      el.addEventListener("click", (e) => {
        active = !active;
        const textSeeMoreEl = e.currentTarget.querySelector(".ct-text-block");
        const iconSeeMoreEl = e.currentTarget.querySelector("svg");

        setSliderViewportHeight(active, index);

        if (active) {
          if (CardPakagesEls[index])
            CardPakagesEls[index].classList.remove("some-hidden");
          if (textSeeMoreEl) textSeeMoreEl.innerHTML = "SEE Less inclusion";
          if (iconSeeMoreEl) iconSeeMoreEl.style.rotate = "180deg";
        } else {
          if (textSeeMoreEl) textSeeMoreEl.innerHTML = "SEE ALL inclusion";
          if (CardPakagesEls[index])
            CardPakagesEls[index].classList.add("some-hidden");
          if (iconSeeMoreEl) iconSeeMoreEl.style.rotate = "";
        }
      });
    });

    packagesEl.style.display = "block";
    new Flickity(packagesSelector, {
      cellAlign: "left",
      contain: true,
      prevNextButtons: false,
      pageDots: false,
    });
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const gallerySelector = ".private-charter--wedding--gallery-main--container";
  const galleryEl = document.querySelector(gallerySelector);

  if (galleryEl) {
    const main = new Flickity(gallerySelector, {
      cellAlign: "center",
      contain: true,
      imagesLoaded: true,
      lazyLoad: true,
      prevNextButtons: false,
      pageDots: false,
      wrapAround: true,
      autoPlay: 2000,
    });

    const buttonNextEl = document.querySelector(
      ".private-charter--wedding--gallery-main--nav-button.next",
    );
    const buttonPrevEl = document.querySelector(
      ".private-charter--wedding--gallery-main--nav-button.prev",
    );

    if (buttonNextEl) {
      buttonNextEl.addEventListener("click", () => {
        main.next();
      });
    }

    if (buttonPrevEl) {
      buttonPrevEl.addEventListener("click", () => {
        main.previous();
      });
    }
  }
});

document.addEventListener("DOMContentLoaded", () => {
  if (window.innerWidth < 1240) {
    const dynamicListEl = document.querySelector("#_dynamic_list-224-420");
    if (dynamicListEl) {
      new Flickity("#_dynamic_list-224-420", {
        cellAlign: "left",
        contain: true,
        prevNextButtons: false,
        pageDots: false,
        imagesLoaded: true,
      });
    }
  }
});
